// Chakra imports
import {
    Avatar,
    Badge,
    Box,
    Flex,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Spinner,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React, {useCallback, useEffect, useState} from "react";
import {FiMoreVertical, FiRefreshCw} from "react-icons/fi";
import {MdLockReset, MdVpnKey, MdBlock, MdCheckCircle} from "react-icons/md";
import {blockUser, resetLoginAttempts, resetPassword, searchUser} from "../../UserSetup/Services";
import ResetPasswordModal from "../../UserSetup/ResetPasswordModal";
import ResetAttemptsModal from "../../UserSetup/ResetAttemptsModal";
import UserBlockDialog from "./UserBlockDialog";

// Status pill styling shared with the rest of the admin user views.
const statusMeta = (status) => {
    switch (status) {
        case "Y":
            return {label: "Active", scheme: "green"};
        case "B":
            return {label: "Blocked", scheme: "red"};
        case "P":
            return {label: "Pending", scheme: "yellow"};
        default:
            return {label: "Inactive", scheme: "gray"};
    }
};

const Conversations = ({selectedSchool, title}) => {
    // Chakra color mode
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    const rowHover = useColorModeValue("gray.50", "whiteAlpha.100");
    const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");

    const toast = useToast();

    const [userList, setUserList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Which user + which dialog is currently open.
    const [activeUser, setActiveUser] = useState(null);
    const [dialog, setDialog] = useState(null); // "BLOCK" | "RESET_PASSWORD" | "RESET_ATTEMPTS"

    const fetchUserList = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await searchUser({schoolId: Number(selectedSchool)}, 1, 10);
            if (response.status === 200) {
                setUserList(response.data?.userList || []);
            }
        } catch (e) {
            console.error("Error while fetching school users:", e);
        } finally {
            setIsLoading(false);
        }
    }, [selectedSchool]);

    useEffect(() => {
        fetchUserList();
    }, [fetchUserList]);

    const openDialog = (user, which) => {
        setActiveUser(user);
        setDialog(which);
    };

    const closeDialog = () => {
        setActiveUser(null);
        setDialog(null);
    };

    const handleBlockUser = async (userId, remarks) => {
        try {
            const response = await blockUser({userId, remarks});
            if (response.status === 200) {
                toast({
                    title: "Done",
                    description: "The user's access was updated.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
                closeDialog();
                fetchUserList();
            }
        } catch (e) {
            console.error("Error while blocking user:", e);
            toast({
                title: "Error",
                description: "Could not update the user. Please try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    };

    const handleResetPassword = async (userId, newPassword) => {
        try {
            const response = await resetPassword({userId: Number(userId), newPassword});
            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Password reset",
                    description: "The user's password was reset successfully.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
                closeDialog();
                fetchUserList();
            }
        } catch (e) {
            console.error("Error while resetting password:", e);
            toast({
                title: "Error",
                description: "Could not reset the password. Please try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            throw e;
        }
    };

    const handleResetAttempts = async (userId, remarks) => {
        try {
            const response = await resetLoginAttempts({userId: Number(userId), remarks});
            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Login attempts reset",
                    description: "The user can sign in again.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
                closeDialog();
                fetchUserList();
            }
        } catch (e) {
            console.error("Error while resetting login attempts:", e);
            toast({
                title: "Error",
                description: "Could not reset the login attempts. Please try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            throw e;
        }
    };

    return (
        <>
            <Card p="16px">
                <CardHeader p="12px 5px" mb="12px">
                    <Flex justify="space-between" align="center" w="100%">
                        <Flex direction="column">
                            <Text fontSize="lg" color={textColor} fontWeight="bold">
                                {title}
                            </Text>
                            <Text fontSize="sm" color={mutedColor}>
                                {userList.length} user{userList.length === 1 ? "" : "s"} in this school
                            </Text>
                        </Flex>
                        <IconButton
                            aria-label="Refresh users"
                            icon={<FiRefreshCw/>}
                            size="sm"
                            variant="ghost"
                            isLoading={isLoading}
                            onClick={fetchUserList}
                        />
                    </Flex>
                </CardHeader>
                <CardBody px="5px">
                    <Flex direction="column" w="100%">
                        {isLoading ? (
                            <Flex justify="center" align="center" py="40px">
                                <Spinner color="teal.300" size="lg"/>
                            </Flex>
                        ) : userList.length === 0 ? (
                            <Flex justify="center" align="center" py="40px">
                                <Text fontSize="sm" color={mutedColor}>
                                    No users found for this school.
                                </Text>
                            </Flex>
                        ) : (
                            userList.map((user) => {
                                const meta = statusMeta(user?.status);
                                const attempts = user?.passwordAttempt;
                                return (
                                    <Flex
                                        key={user?.id}
                                        justify="space-between"
                                        align="center"
                                        gap="10px"
                                        p="10px"
                                        borderRadius="12px"
                                        transition="background 0.15s ease"
                                        _hover={{bg: rowHover}}
                                        _notLast={{borderBottom: "1px solid", borderColor}}
                                    >
                                        <Flex align="center" minW="0" gap="12px">
                                            <Avatar
                                                src={user?.image}
                                                name={user?.fullName}
                                                w="46px"
                                                h="46px"
                                                borderRadius="14px"
                                            />
                                            <Flex direction="column" minW="0">
                                                <Flex align="center" gap="8px" wrap="wrap">
                                                    <Text fontSize="sm" color={textColor} fontWeight="bold" noOfLines={1}>
                                                        {user?.fullName}
                                                    </Text>
                                                    <Badge
                                                        colorScheme={meta.scheme}
                                                        variant="subtle"
                                                        borderRadius="full"
                                                        px="8px"
                                                        fontSize="10px"
                                                        textTransform="none"
                                                    >
                                                        {meta.label}
                                                    </Badge>
                                                </Flex>
                                                <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                                                    {user?.email}
                                                    {user?.roleCode ? ` · ${user.roleCode}` : ""}
                                                </Text>
                                                {attempts > 0 && (
                                                    <Text fontSize="xs" color="orange.400" fontWeight="500">
                                                        {attempts} failed login attempt{attempts === 1 ? "" : "s"}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Flex>

                                        <Menu placement="bottom-end" isLazy>
                                            <MenuButton
                                                as={IconButton}
                                                aria-label={`Actions for ${user?.fullName}`}
                                                icon={<FiMoreVertical/>}
                                                size="sm"
                                                variant="ghost"
                                            />
                                            <MenuList minW="180px">
                                                <MenuItem
                                                    icon={<Icon as={MdLockReset} boxSize="18px"/>}
                                                    onClick={() => openDialog(user, "RESET_ATTEMPTS")}
                                                >
                                                    Reset Attempts
                                                </MenuItem>
                                                <MenuItem
                                                    icon={<Icon as={MdVpnKey} boxSize="18px"/>}
                                                    onClick={() => openDialog(user, "RESET_PASSWORD")}
                                                >
                                                    Reset Password
                                                </MenuItem>
                                                <MenuDivider/>
                                                <MenuItem
                                                    icon={
                                                        <Icon
                                                            as={user?.status === "B" ? MdCheckCircle : MdBlock}
                                                            boxSize="18px"
                                                        />
                                                    }
                                                    color={user?.status === "B" ? "green.400" : "red.400"}
                                                    onClick={() => openDialog(user, "BLOCK")}
                                                >
                                                    {user?.status === "B" ? "Unblock" : "Block"}
                                                </MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </Flex>
                                );
                            })
                        )}
                    </Flex>
                </CardBody>
            </Card>

            {dialog === "BLOCK" && activeUser && (
                <UserBlockDialog
                    isOpen
                    onClose={closeDialog}
                    user={activeUser}
                    handleBlockUser={handleBlockUser}
                />
            )}
            {dialog === "RESET_PASSWORD" && activeUser && (
                <ResetPasswordModal
                    isOpen
                    onClose={closeDialog}
                    user={activeUser}
                    handleResetPassword={handleResetPassword}
                />
            )}
            {dialog === "RESET_ATTEMPTS" && activeUser && (
                <ResetAttemptsModal
                    isOpen
                    onClose={closeDialog}
                    user={activeUser}
                    handleResetAttempts={handleResetAttempts}
                />
            )}
        </>
    );
};

export default Conversations;
